import { Injectable, signal, effect, OnDestroy, inject } from '@angular/core';
import { PlatformService } from './platform.service';
import { AuthService } from './auth.service';
import { StorageService } from './storage/storage.service';
import { UserAppearance } from '@features/authentication/models/authentication.model';

export type Theme = 'bright' | 'dark' | 'adaptive';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private readonly THEME_ATTRIBUTE = 'data-theme';
  private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null;
  private readonly authService = inject(AuthService);
  private readonly platformService = inject(PlatformService);
  private readonly storageService = inject(StorageService);
  private isSyncingFromAuth = false; // Flag to prevent circular updates

  // Signal to track current theme (using UI enum values: bright, dark, adaptive)
  readonly theme = signal<Theme>(this.getInitialTheme());
  readonly effectiveTheme = signal<'light' | 'dark'>('light');

  constructor() {
    // Apply theme on initialization
    this.applyTheme(this.theme());

    // Watch for theme changes and apply them
    effect(() => {
      this.applyTheme(this.theme());
    });
  }

  ngOnDestroy(): void {
    if (this.systemThemeListener && this.platformService.isBrowser()) {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', this.systemThemeListener);
      this.systemThemeListener = null;
    }
  }

  /**
   * Get initial theme from userSettings.appearance or system preference
   */
  private getInitialTheme(): Theme {
    if (!this.platformService.isBrowser()) {
      return 'bright';
    }

    // First, try to get from AuthService userSettings
    const userData = this.authService.authState().userData;
    if (userData?.userSettings?.appearance) {
      // Map AuthService UserAppearance enum to Theme type
      if (userData.userSettings.appearance === UserAppearance.bright) {
        return 'bright';
      } else if (userData.userSettings.appearance === UserAppearance.dark) {
        return 'dark';
      } else {
        return 'adaptive';
      }
    }

    // Fallback: try to get from StorageService 'appearance' key (for backward compatibility)
    const saved = this.storageService.getItem('appearance');
    if (saved && (saved === 'bright' || saved === 'dark' || saved === 'adaptive')) {
      return saved as Theme;
    }

    // Default to adaptive (system preference)
    return 'adaptive';
  }

  /**
   * Toggle between bright and dark themes (skip adaptive)
   */
  toggleTheme(): void {
    const current = this.theme();
    let newTheme: Theme;
    if (current === 'adaptive') {
      // If adaptive, toggle to opposite of current effective theme
      newTheme = this.effectiveTheme() === 'light' ? 'dark' : 'bright';
    } else {
      newTheme = current === 'bright' ? 'dark' : 'bright';
    }
    this.setTheme(newTheme);
  }

  /**
   * Sync theme from AuthService userSettings.appearance (called externally when needed)
   */
  syncFromAuthService(): void {
    const userData = this.authService.authState().userData;
    if (userData?.userSettings?.appearance) {
      // Map AuthService UserAppearance enum to Theme type
      let themeValue: Theme;
      if (userData.userSettings.appearance === UserAppearance.bright) {
        themeValue = 'bright';
      } else if (userData.userSettings.appearance === UserAppearance.dark) {
        themeValue = 'dark';
      } else {
        themeValue = 'adaptive';
      }
      
      // Only update if different
      if (this.theme() !== themeValue) {
        this.isSyncingFromAuth = true;
        this.theme.set(themeValue);
        queueMicrotask(() => {
          this.isSyncingFromAuth = false;
        });
      }
    }
  }

  /**
   * Set theme explicitly (does not save - let account-settings component handle saving)
   */
  setTheme(theme: Theme): void {
    this.theme.set(theme);
    // Also save to StorageService 'appearance' key for backward compatibility
    if (this.platformService.isBrowser()) {
      this.storageService.setItem('appearance', theme);
    }
  }

  /**
   * Apply theme to the document
   * Maps UI enum values (bright, dark, adaptive) to DOM values (light, dark, system)
   */
  private applyTheme(theme: Theme): void {
    if (!this.platformService.isBrowser() || typeof document === 'undefined') {
      return;
    }

    const htmlElement = document.documentElement;

    // Remove previous system theme listener if exists
    if (this.systemThemeListener) {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', this.systemThemeListener);
      this.systemThemeListener = null;
    }

    if (theme === 'adaptive') {
      // Map 'adaptive' to 'system' behavior
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.effectiveTheme.set(prefersDark ? 'dark' : 'light');
      
      if (prefersDark) {
        htmlElement.classList.add('dark');
        htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'dark');
      } else {
        htmlElement.classList.remove('dark');
        htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'light');
      }

      // Listen for system theme changes
      this.systemThemeListener = (e: MediaQueryListEvent) => {
        this.effectiveTheme.set(e.matches ? 'dark' : 'light');
        if (e.matches) {
          htmlElement.classList.add('dark');
          htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'dark');
        } else {
          htmlElement.classList.remove('dark');
          htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'light');
        }
      };
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', this.systemThemeListener);
    } else {
      // Map 'bright' to 'light' for DOM, 'dark' stays 'dark'
      const domTheme = theme === 'bright' ? 'light' : theme;
      this.effectiveTheme.set(domTheme);
      if (theme === 'dark') {
        htmlElement.classList.add('dark');
        htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'dark');
      } else {
        htmlElement.classList.remove('dark');
        htmlElement.setAttribute(this.THEME_ATTRIBUTE, 'light');
      }
    }
  }


  /**
   * Check if current theme is dark
   */
  isDark(): boolean {
    return this.effectiveTheme() === 'dark';
  }
}



